// Admin console: Grup Perusahaan (/admin/grup-perusahaan), read only.
//   header   plain spacing   h1 and how many groups
//   list     rows            name, owner, members, documents, created; Lihat
//   detail   dialog          members with their roles, open invitations, and
//                            the documents' metadata (never their text)
// Changing a group (remove a member, change a role, delete) has API routes
// (/admin/company-groups) but no screen yet: the user runs the group itself.
import { useState } from "react";
import * as API from "../api";
import type { AdminCompanyGroupDetail, AdminCompanyGroupSummary } from "../api";
import { DOC_TYPE_LABEL, INDEX_LABEL, INDEX_TONE, ROLE_LABEL } from "../company/labels";
import { formatCount, formatDate } from "../lib/format";
import { formatFileSize } from "../project/files";
import { Badge, Dialog, EmptyState, Notice, PageHeader, SkeletonRows, useAsync, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";

function GroupDetail({ id }: { id: string }) {
  const detail = useAsync<AdminCompanyGroupDetail>(() => API.getAdminCompanyGroup(id), [id]);
  if (detail.loading && !detail.data) return <SkeletonRows count={3} label="Memuat grup" />;
  if (detail.error || !detail.data) {
    return (
      <Notice tone="danger" title="Grup belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={detail.reload}>Muat ulang</button>}>
        <p>{API.errorMessage(detail.error)}</p>
      </Notice>
    );
  }
  const d = detail.data;
  return (
    <div className="form-stack">
      <h3 className="section-title" id="acg-members">
        Anggota ({d.members.length})
      </h3>
      <ul className="rows" aria-labelledby="acg-members">
        {d.members.map((m) => (
          <li key={m.userId} className="row">
            <span className="row-main">
              <span className="row-title">{m.name}</span>
              <span className="row-meta">
                <Badge tone={m.role === "member" ? "neutral" : "info"}>{ROLE_LABEL[m.role]}</Badge> {m.email} · bergabung {formatDate(m.joinedAt)}
              </span>
            </span>
          </li>
        ))}
      </ul>
      <h3 className="section-title" id="acg-invites">
        Undangan terbuka ({d.invites.length})
      </h3>
      {d.invites.length === 0 ? (
        <p className="form-note">Tidak ada undangan yang menunggu.</p>
      ) : (
        <ul className="rows" aria-labelledby="acg-invites">
          {d.invites.map((i) => (
            <li key={i.id} className="row">
              <span className="row-main">
                <span className="row-title">{i.email}</span>
                <span className="row-meta">{[i.invitedBy ? `Diundang ${i.invitedBy.name}` : "", formatDate(i.createdAt)].filter(Boolean).join(" · ")}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      <h3 className="section-title" id="acg-docs">
        Dokumen ({d.documents.length})
      </h3>
      {d.documents.length === 0 ? (
        <p className="form-note">Belum ada dokumen grup.</p>
      ) : (
        <ul className="rows" aria-labelledby="acg-docs">
          {d.documents.map((doc) => (
            <li key={doc.id} className="row">
              <span className="row-main">
                <span className="row-title">{doc.title}</span>
                <span className="row-meta">
                  <Badge>{doc.docTypeLabel ?? (doc.docType ? DOC_TYPE_LABEL[doc.docType] : "Jenis belum ditentukan")}</Badge> <Badge tone={INDEX_TONE[doc.indexStatus]}>{INDEX_LABEL[doc.indexStatus]}</Badge>{" "}
                  {[doc.uploadedBy?.name ?? "", formatDate(doc.createdAt), formatFileSize(doc.sizeBytes)].filter(Boolean).join(" · ")}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function CompanyGroupsAdminScreen() {
  useTitle("Grup Perusahaan");
  const list = useAsync<AdminCompanyGroupSummary[]>(() => API.getAdminCompanyGroups(), []);
  const [open, setOpen] = useState<AdminCompanyGroupSummary | null>(null);
  const groups = list.data ?? [];
  return (
    <>
      <PageHeader divided title="Grup Perusahaan" lead={list.data ? `${formatCount(groups.length)} grup. Grup dikelola pemilik dan admin grup dari halaman Data Perusahaan.` : "Memuat grup perusahaan"} />
      {list.loading && !list.data ? (
        <SkeletonRows count={4} label="Memuat grup perusahaan" />
      ) : list.error ? (
        <Notice tone="danger" title="Grup perusahaan belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={list.reload}>Muat ulang</button>}>
          <p>{API.errorMessage(list.error)}</p>
        </Notice>
      ) : groups.length === 0 ? (
        <EmptyState icon="building" title="Belum ada grup perusahaan" body="Grup muncul di sini setelah pengguna membuatnya di Data Perusahaan." />
      ) : (
        <ul className="rows" aria-label="Grup perusahaan">
          {groups.map((g) => (
            <li key={g.id} className="row">
              <span className="row-main">
                <span className="row-title">{g.name}</span>
                <span className="row-meta">
                  {[g.owner ? `Pemilik ${g.owner.name} (${g.owner.email})` : "Tanpa pemilik", `${formatCount(g.members)} anggota`, `${formatCount(g.documents)} dokumen`, `dibuat ${formatDate(g.createdAt)}`].join(" · ")}
                </span>
              </span>
              <span className="row-side">
                <button type="button" className="btn btn-secondary" aria-label={`Lihat ${g.name}`} onClick={() => setOpen(g)}>
                  <Glyph name="eye" />
                  <span>Lihat</span>
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <Dialog open={open !== null} title={open?.name ?? ""} description="Hanya baca. Isi dokumen tidak ditampilkan." onClose={() => setOpen(null)} size="lg">
        {open ? <GroupDetail id={open.id} /> : null}
      </Dialog>
    </>
  );
}
